import { useEffect, useMemo, useState } from 'react';
import { useContext } from 'react';
import PushSubscribeButton from '../pushSubscribeButton/PushSubscribeButton.jsx';
import useWebPushStatus from './useWebPushStatus';
import { AuthContext } from '../../context/AuthContext';
import PresenceSettingsModal, {
  presenceTooltipCopy,
} from '../presence/PresenceSettingsModal';

const RESURFACE_MS = 24 * 60 * 60 * 1000;

export default function PushNudgeRail({
  context = 'connect',
  product = 'connect',
  hideWhenSubscribed = false,
}) {
  const { player } = useContext(AuthContext);
  const state = useWebPushStatus();
  const [showPresenceSettings, setShowPresenceSettings] = useState(false);
  const dismissKey = useMemo(
    () => `pushNudgeRail:dismissedAt:${product}`,
    [product],
  );

  const [dismissedAt, setDismissedAt] = useState(() => {
    const raw = localStorage.getItem(dismissKey);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  });

  const dismissedRecently =
    dismissedAt > 0 && Date.now() - dismissedAt < RESURFACE_MS;

  const handleDismiss = () => {
    const ts = Date.now();
    setDismissedAt(ts);
    localStorage.setItem(dismissKey, String(ts));
  };

  useEffect(() => {
    if (!dismissedAt) return;
    const now = Date.now();
    const remaining = RESURFACE_MS - (now - dismissedAt);
    if (remaining <= 0) {
      localStorage.removeItem(dismissKey);
      setDismissedAt(0);
      return;
    }
    const timer = setTimeout(() => {
      localStorage.removeItem(dismissKey);
      setDismissedAt(0);
    }, remaining);
    return () => clearTimeout(timer);
  }, [dismissedAt, dismissKey]);

  const shouldShowFull =
    state.hydrated && !dismissedRecently && !state.subscribed;
  const isLocalOpsContext = context === 'localops';
  const alertCopy = {
    title: isLocalOpsContext ? 'Enable LocalOps alerts' : 'Enable JG alerts',
    meta: isLocalOpsContext
      ? 'Get notified about nearby tasks and important updates, even when Joe Galaxy is closed.'
      : 'Messages, requests, LocalOps tasks, and updates.',
    chipLabel: isLocalOpsContext ? 'LocalOps alerts' : 'Alerts',
    product: isLocalOpsContext ? 'localops' : product,
  };

  if (state.subscribed) {
    return hideWhenSubscribed ? null : (
      <div className="feedHighlightCard feedHighlightCard--chip">
        <span className="feedHighlightChipLabel">{alertCopy.chipLabel}</span>
        <span className="feedHighlightChipValue">
          Alerts enabled on this device
        </span>
      </div>
    );
  }

  if (dismissedRecently && !state.subscribed) {
    return (
      <div className="feedHighlightCard feedHighlightCard--bell">
        <span className="feedHighlightBell">Bell</span>
        <span className="feedHighlightMeta">{alertCopy.chipLabel}</span>
      </div>
    );
  }

  if (!shouldShowFull) return null;

  return (
    <>
      <div className="feedHighlightCard feedHighlightCard--push jgPushNudge">
        <div className="feedHighlightTitleRow">
          <span className="feedHighlightTitle">{alertCopy.title}</span>
          <button
            type="button"
            className="feedHighlightInfo"
            title={presenceTooltipCopy}
            onClick={(event) => {
              event.stopPropagation();
              setShowPresenceSettings(true);
            }}
            aria-label="Presence info"
          >
            i
          </button>
        </div>
        <div className="feedHighlightActionsRow">
          <PushSubscribeButton
            jwt={player?.token}
            vapidKey={process.env.REACT_APP_VAPID_PUBLIC_KEY}
            product={alertCopy.product}
            copy={{ helpText: alertCopy.meta }}
            variant="compact"
          />
          <button
            type="button"
            className="feedHighlightDismiss"
            onClick={handleDismiss}
          >
            Not now
          </button>
        </div>
      </div>
      <PresenceSettingsModal
        open={showPresenceSettings}
        onClose={() => setShowPresenceSettings(false)}
      />
    </>
  );
}
